import { createBrowserRouter, Navigate, Outlet } from 'react-router';
import { RequireAuthority } from '@/shared/components/require-authority';
import { RouteErrorBoundary } from '@/shared/components/route-error-boundary';
import { DefaultLandingRedirect } from '@/app/default-landing';
import { PreferencesPage } from '@/modules/me/preferences';
import { LoginPage } from '@/modules/auth/login';
import { ChangePasswordPage } from '@/modules/auth/change-password';
import { AppLayout } from '@/app/layout';
import { DashboardPage } from '@/app/dashboard';
import { SetupHubPage } from '@/modules/setup';
import { AcademicYearsPage } from '@/modules/setup/academic-years';
import { ClassesPage } from '@/modules/setup/classes';
import { ClassSectionsPage } from '@/modules/setup/class-sections';
import { CitiesPage } from '@/modules/setup/cities';
import { LookupsPage } from '@/modules/setup/lookups';
import { SystemSettingsPage } from '@/modules/setup/system-settings';
import { StudentsListPage } from '@/modules/students/list';
import { NewStudentPage } from '@/modules/students/new';
import { StudentDetailPage } from '@/modules/students/detail';
import { StudentsImportPage } from '@/modules/students/import';
import { AttendanceHubPage } from '@/modules/attendance';
import { DailyAttendancePage } from '@/modules/attendance/daily';
import { MonthlyAttendancePage } from '@/modules/attendance/monthly';
import { HolidaysPage } from '@/modules/attendance/holidays';
import { FeeHubPage } from '@/modules/fee';
import { FeeTypesPage } from '@/modules/fee/types';
import { FeeStructuresPage } from '@/modules/fee/structures';
import { FeeGeneratePage } from '@/modules/fee/generate';
import { FeeCollectPage } from '@/modules/fee/collect';
import { FeeStudentPage } from '@/modules/fee/student';
import { FeeCashierPage } from '@/modules/fee/cashier';
import { FeeDefaultersPage } from '@/modules/fee/defaulters';
import { FeeReceiptPrintPage } from '@/modules/fee/receipt-print';
import { FeeVouchersPage } from '@/modules/fee/vouchers';
import { FinanceHubPage } from '@/modules/finance';
import { FinanceAccountsPage } from '@/modules/finance/accounts';
import { FinanceBankAccountsPage } from '@/modules/finance/bank-accounts';
import { FinanceExpensesPage } from '@/modules/finance/expenses';
import { FinanceOtherIncomePage } from '@/modules/finance/other-income';
import { FinanceJournalsPage } from '@/modules/finance/journals';
import { FinanceDaybookPage } from '@/modules/finance/daybook';
import { FinanceTrialBalancePage } from '@/modules/finance/trial-balance';
import { FinanceReconciliationPage } from '@/modules/finance/reconciliation';
import { FinanceIncomeExpenditurePage } from '@/modules/finance/income-expenditure';
import { FinanceBalanceSheetPage } from '@/modules/finance/balance-sheet';
import { FinanceBankBookPage } from '@/modules/finance/bank-book';
import { FinanceCashHandoverPage } from '@/modules/finance/cash-handover';
import { HrHubPage } from '@/modules/hr';
import { HrStaffListPage } from '@/modules/hr/staff-list';
import { HrStaffNewPage } from '@/modules/hr/staff-new';
import { HrStaffDetailPage } from '@/modules/hr/staff-detail';
import { HrDesignationsPage } from '@/modules/hr/designations';
import { HrLeavePage } from '@/modules/hr/leave';
import { SalaryComponentsPage } from '@/modules/hr/salary-components';
import { SalaryStructurePage } from '@/modules/hr/salary-structure';
import { SalaryAdvancesPage } from '@/modules/hr/salary-advances';
import { PayrollPage } from '@/modules/hr/payroll';
import { StaffAttendancePage } from '@/modules/hr/staff-attendance';
import { EvaluationCriteriaPage } from '@/modules/hr/evaluation-criteria';
import { PerformanceEvaluationsPage } from '@/modules/hr/evaluations';
import { ReportsHubPage } from '@/modules/reports';
import { FeeCollectionReportPage } from '@/modules/reports/fee-collection';
import { CashBookReportPage } from '@/modules/reports/cash-book';
import { BackdatedReceiptsReportPage } from '@/modules/reports/backdated-receipts';
import { PayrollRegisterReportPage } from '@/modules/reports/payroll-register';
import { LowStockReportPage } from '@/modules/reports/low-stock';
import { ChronicAbsenteesPage } from '@/modules/reports/chronic-absentees';
import { ReceivablesAgingPage } from '@/modules/reports/receivables-aging';
import { ConcessionsReportPage } from '@/modules/reports/concessions';
import { CollectionTrendReportPage } from '@/modules/reports/collection-trend';
import { FeeTypeYieldReportPage } from '@/modules/reports/fee-type-yield';
import { AcademicsHubPage } from '@/modules/academics';
import { PeriodSlotsPage } from '@/modules/academics/period-slots';
import { GradingScalesPage } from '@/modules/academics/grading-scales';
import { ClassSubjectsPage } from '@/modules/academics/class-subjects';
import { WeightagesPage } from '@/modules/academics/weightages';
import { TimetablePage } from '@/modules/academics/timetable';
import { ExamSchedulePage } from '@/modules/academics/exam-schedule';
import { MarksEntryPage } from '@/modules/academics/marks';
import { ResultsPage } from '@/modules/academics/results';
import { PromotionPage } from '@/modules/academics/promotion';
import { InventoryHubPage } from '@/modules/inventory';
import { VendorsPage } from '@/modules/inventory/vendors';
import { VendorAgingPage } from '@/modules/inventory/vendor-aging';
import { VendorLedgerPage } from '@/modules/inventory/vendor-ledger';
import { AuthEventsPage } from '@/modules/audit/auth-events';
import { AuditHistoryPage } from '@/modules/audit/history';
import { StoreHubPage } from '@/modules/inventory/store';
import { AssetsPage } from '@/modules/inventory/assets';
import { ConsumablesPage } from '@/modules/inventory/consumables';
import { NotificationsHubPage } from '@/modules/notifications';
import { SmsQueuePage } from '@/modules/notifications/sms';
import { GatewayDevicesPage } from '@/modules/notifications/devices';
import { FrontDeskHubPage } from '@/modules/frontdesk';
import { VisitorsPage } from '@/modules/frontdesk/visitors';
import { InquiriesPage } from '@/modules/frontdesk/inquiries';
import { ComplaintsPage } from '@/modules/frontdesk/complaints';
import { IssuancesPage } from '@/modules/frontdesk/issuances';
import { NoticesPage } from '@/modules/frontdesk/notices';
import { DocumentTypesPage } from '@/modules/frontdesk/document-types';
import { UsersPage } from '@/modules/usermgmt/users';
import { RolesPage } from '@/modules/usermgmt/roles';
import { RolePermissionsPage } from '@/modules/usermgmt/role-permissions';

export const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
  { path: '/change-password', element: <ChangePasswordPage /> },
  // Print routes render outside AppLayout — no sidebar/header so window.print() captures only the receipt.
  {
    path: '/fee/receipts/:id/print',
    element: (
      <RequireAuthority authority="FEE_MGMT:VIEW">
        <FeeReceiptPrintPage />
      </RequireAuthority>
    ),
  },
  {
    path: '/',
    element: <AppLayout />,
    /**
     * Any render error from a child route bubbles up here. The boundary keeps
     * the chrome (sidebar / header / breadcrumb) and replaces only the page
     * content area with a friendly fallback — much less jarring than the
     * default whitescreen, and the user can still navigate away via the
     * sidebar without losing their session.
     */
    errorElement: <RouteErrorBoundary />,
    children: [
      { index: true, element: <DefaultLandingRedirect /> },
      { path: 'dashboard', element: <DashboardPage />, handle: { crumb: 'Dashboard' } },
      { path: 'me/preferences', element: <PreferencesPage />, handle: { crumb: 'Preferences' } },
      {
        path: 'students',
        element: <RequireAuthority authority="STUDENT_MGMT:VIEW"><Outlet /></RequireAuthority>,
        handle: { crumb: 'Students' },
        children: [
          { index: true, element: <StudentsListPage />, handle: { newPath: '/students/new' } },
          { path: 'new', element: <NewStudentPage />, handle: { crumb: 'New' } },
          { path: 'import', element: <StudentsImportPage />, handle: { crumb: 'Import' } },
          { path: ':id', element: <StudentDetailPage />, handle: { crumb: (p: { id?: string }) => `#${p.id}` } },
        ],
      },
      {
        path: 'attendance',
        element: <RequireAuthority authority="ATTENDANCE:VIEW"><Outlet /></RequireAuthority>,
        handle: { crumb: 'Attendance' },
        children: [
          { index: true, element: <AttendanceHubPage /> },
          { path: 'daily', element: <DailyAttendancePage />, handle: { crumb: 'Daily' } },
          { path: 'monthly', element: <MonthlyAttendancePage />, handle: { crumb: 'Monthly' } },
          { path: 'holidays', element: <HolidaysPage />, handle: { crumb: 'Holidays' } },
        ],
      },
      {
        path: 'academics',
        element: <RequireAuthority authority="ACADEMICS:VIEW"><Outlet /></RequireAuthority>,
        handle: { crumb: 'Academics' },
        children: [
          { index: true, element: <AcademicsHubPage /> },
          { path: 'period-slots', element: <PeriodSlotsPage />, handle: { crumb: 'Period slots' } },
          { path: 'grading-scales', element: <GradingScalesPage />, handle: { crumb: 'Grading scales' } },
          { path: 'class-subjects', element: <ClassSubjectsPage />, handle: { crumb: 'Class subjects' } },
          { path: 'weightages', element: <WeightagesPage />, handle: { crumb: 'Weightages' } },
          { path: 'timetable', element: <TimetablePage />, handle: { crumb: 'Timetable' } },
          { path: 'exam-schedule', element: <ExamSchedulePage />, handle: { crumb: 'Exam schedule' } },
          { path: 'marks', element: <MarksEntryPage />, handle: { crumb: 'Marks' } },
          { path: 'results', element: <ResultsPage />, handle: { crumb: 'Results' } },
          { path: 'promotion', element: <PromotionPage />, handle: { crumb: 'Promotion' } },
        ],
      },
      {
        path: 'fee',
        element: <RequireAuthority authority="FEE_MGMT:VIEW"><Outlet /></RequireAuthority>,
        handle: { crumb: 'Fee' },
        children: [
          { index: true, element: <FeeHubPage /> },
          { path: 'types', element: <FeeTypesPage />, handle: { crumb: 'Types' } },
          { path: 'structures', element: <FeeStructuresPage />, handle: { crumb: 'Structures' } },
          { path: 'generate', element: <FeeGeneratePage />, handle: { crumb: 'Generate' } },
          { path: 'collect', element: <FeeCollectPage />, handle: { crumb: 'Collect' } },
          { path: 'cashier/:enrollmentId', element: <FeeCashierPage />, handle: { crumb: 'Cashier' } },
          { path: 'student/:enrollmentId', element: <FeeStudentPage />, handle: { crumb: 'Student ledger' } },
          { path: 'defaulters', element: <FeeDefaultersPage />, handle: { crumb: 'Defaulters' } },
          { path: 'vouchers', element: <FeeVouchersPage />, handle: { crumb: 'Vouchers' } },
        ],
      },
      {
        path: 'finance',
        element: <RequireAuthority authority="FINANCE:VIEW"><Outlet /></RequireAuthority>,
        handle: { crumb: 'Finance' },
        children: [
          { index: true, element: <FinanceHubPage /> },
          { path: 'accounts', element: <FinanceAccountsPage />, handle: { crumb: 'Accounts' } },
          { path: 'bank-accounts', element: <FinanceBankAccountsPage />, handle: { crumb: 'Bank accounts' } },
          { path: 'expenses', element: <FinanceExpensesPage />, handle: { crumb: 'Expenses' } },
          { path: 'other-income', element: <FinanceOtherIncomePage />, handle: { crumb: 'Other income' } },
          { path: 'journals', element: <FinanceJournalsPage />, handle: { crumb: 'Journals' } },
          { path: 'daybook', element: <FinanceDaybookPage />, handle: { crumb: 'Daybook' } },
          { path: 'trial-balance', element: <FinanceTrialBalancePage />, handle: { crumb: 'Trial balance' } },
          { path: 'reconciliation', element: <FinanceReconciliationPage />, handle: { crumb: 'Reconciliation' } },
          { path: 'income-expenditure', element: <FinanceIncomeExpenditurePage />, handle: { crumb: 'Income & expenditure' } },
          { path: 'balance-sheet', element: <FinanceBalanceSheetPage />, handle: { crumb: 'Balance sheet' } },
          { path: 'bank-book', element: <FinanceBankBookPage />, handle: { crumb: 'Bank book' } },
          { path: 'cash-handover', element: <FinanceCashHandoverPage />, handle: { crumb: 'Cash handover' } },
        ],
      },
      {
        path: 'hr',
        element: <RequireAuthority authority="HR_MGMT:VIEW"><Outlet /></RequireAuthority>,
        handle: { crumb: 'HR' },
        children: [
          { index: true, element: <HrHubPage /> },
          { path: 'staff', element: <HrStaffListPage />, handle: { crumb: 'Staff', newPath: '/hr/staff/new' } },
          { path: 'staff/new', element: <HrStaffNewPage />, handle: { crumb: 'New staff' } },
          { path: 'staff/:id', element: <HrStaffDetailPage />, handle: { crumb: (p: { id?: string }) => `Staff #${p.id}` } },
          { path: 'designations', element: <HrDesignationsPage />, handle: { crumb: 'Designations' } },
          { path: 'leave', element: <HrLeavePage />, handle: { crumb: 'Leave' } },
          { path: 'salary-components', element: <SalaryComponentsPage />, handle: { crumb: 'Salary components' } },
          { path: 'salary-structure', element: <SalaryStructurePage />, handle: { crumb: 'Salary structure' } },
          { path: 'salary-advances', element: <SalaryAdvancesPage />, handle: { crumb: 'Salary advances' } },
          { path: 'payroll', element: <PayrollPage />, handle: { crumb: 'Payroll' } },
          { path: 'attendance', element: <StaffAttendancePage />, handle: { crumb: 'Staff attendance' } },
          { path: 'evaluation-criteria', element: <EvaluationCriteriaPage />, handle: { crumb: 'Evaluation criteria' } },
          { path: 'evaluations', element: <PerformanceEvaluationsPage />, handle: { crumb: 'Evaluations' } },
        ],
      },
      {
        path: 'inventory',
        element: <RequireAuthority authority="INVENTORY:VIEW"><Outlet /></RequireAuthority>,
        handle: { crumb: 'Inventory' },
        children: [
          { index: true, element: <InventoryHubPage /> },
          { path: 'vendors', element: <VendorsPage />, handle: { crumb: 'Vendors' } },
          { path: 'vendors/aging', element: <VendorAgingPage />, handle: { crumb: 'Vendor aging' } },
          { path: 'vendors/ledger', element: <VendorLedgerPage />, handle: { crumb: 'Vendor ledger' } },
          { path: 'store', element: <StoreHubPage />, handle: { crumb: 'Store' } },
          { path: 'store/outstanding', element: <StoreHubPage />, handle: { crumb: 'Store outstanding' } },
          { path: 'assets', element: <AssetsPage />, handle: { crumb: 'Assets' } },
          { path: 'consumables', element: <ConsumablesPage />, handle: { crumb: 'Consumables' } },
        ],
      },
      {
        path: 'notifications',
        element: <RequireAuthority authority="NOTIFICATIONS:VIEW"><Outlet /></RequireAuthority>,
        handle: { crumb: 'Notifications' },
        children: [
          { index: true, element: <NotificationsHubPage /> },
          { path: 'sms', element: <SmsQueuePage />, handle: { crumb: 'SMS queue' } },
          { path: 'devices', element: <GatewayDevicesPage />, handle: { crumb: 'Gateway devices' } },
        ],
      },
      {
        path: 'frontdesk',
        element: <RequireAuthority authority="FRONT_DESK:VIEW"><Outlet /></RequireAuthority>,
        handle: { crumb: 'Front desk' },
        children: [
          { index: true, element: <FrontDeskHubPage /> },
          { path: 'visitors', element: <VisitorsPage />, handle: { crumb: 'Visitors' } },
          { path: 'inquiries', element: <InquiriesPage />, handle: { crumb: 'Inquiries' } },
          { path: 'complaints', element: <ComplaintsPage />, handle: { crumb: 'Complaints' } },
          { path: 'issuances', element: <IssuancesPage />, handle: { crumb: 'Issuances' } },
          { path: 'notices', element: <NoticesPage />, handle: { crumb: 'Notices' } },
          { path: 'document-types', element: <DocumentTypesPage />, handle: { crumb: 'Document types' } },
        ],
      },
      {
        path: 'reports',
        element: <RequireAuthority authority="REPORTS:VIEW"><Outlet /></RequireAuthority>,
        handle: { crumb: 'Reports' },
        children: [
          { index: true, element: <ReportsHubPage /> },
          { path: 'fee-collection', element: <FeeCollectionReportPage />, handle: { crumb: 'Fee collection' } },
          { path: 'cash-book', element: <CashBookReportPage />, handle: { crumb: 'Cash book' } },
          { path: 'backdated-receipts', element: <BackdatedReceiptsReportPage />, handle: { crumb: 'Back-dated receipts' } },
          { path: 'payroll-register', element: <PayrollRegisterReportPage />, handle: { crumb: 'Payroll register' } },
          { path: 'low-stock', element: <LowStockReportPage />, handle: { crumb: 'Low stock' } },
          { path: 'chronic-absentees', element: <ChronicAbsenteesPage />, handle: { crumb: 'Chronic absentees' } },
          { path: 'receivables-aging', element: <ReceivablesAgingPage />, handle: { crumb: 'Receivables aging' } },
          { path: 'concessions', element: <ConcessionsReportPage />, handle: { crumb: 'Concessions' } },
          { path: 'collection-trend', element: <CollectionTrendReportPage />, handle: { crumb: 'Collection trend' } },
          { path: 'fee-type-yield', element: <FeeTypeYieldReportPage />, handle: { crumb: 'Fee-type yield' } },
        ],
      },
      {
        path: 'setup',
        element: <RequireAuthority authority="SETUP:VIEW"><Outlet /></RequireAuthority>,
        handle: { crumb: 'Setup' },
        children: [
          { index: true, element: <SetupHubPage /> },
          { path: 'academic-years', element: <AcademicYearsPage />, handle: { crumb: 'Academic years' } },
          { path: 'classes', element: <ClassesPage />, handle: { crumb: 'Classes' } },
          { path: 'class-sections', element: <ClassSectionsPage />, handle: { crumb: 'Class sections' } },
          { path: 'cities', element: <CitiesPage />, handle: { crumb: 'Cities' } },
          { path: 'lookups', element: <LookupsPage />, handle: { crumb: 'Lookups' } },
          { path: 'lookups/:slug', element: <LookupsPage />, handle: { crumb: (p: { slug?: string }) => p.slug ?? 'Lookup' } },
          { path: 'system-settings', element: <SystemSettingsPage />, handle: { crumb: 'System settings' } },
        ],
      },
      {
        path: 'users',
        element: <RequireAuthority authority="USER_MGMT:VIEW"><Outlet /></RequireAuthority>,
        handle: { crumb: 'Users' },
        children: [
          { index: true, element: <UsersPage /> },
          { path: 'roles', element: <RolesPage />, handle: { crumb: 'Roles' } },
          { path: 'roles/:id', element: <RolePermissionsPage />, handle: { crumb: 'Permissions' } },
        ],
      },
      {
        // V15 — audit log (record history, auth events) is Super Admin only.
        // Principal lost access along with Setup / User Mgmt / Finance / Inventory.
        path: 'audit',
        element: <RequireAuthority authority="ROLE_SUPER_ADMIN"><Outlet /></RequireAuthority>,
        handle: { crumb: 'Audit' },
        children: [
          { index: true, element: <Navigate to="/audit/auth-events" replace /> },
          { path: 'auth-events', element: <AuthEventsPage />, handle: { crumb: 'Auth events' } },
          { path: 'history', element: <AuditHistoryPage />, handle: { crumb: 'Record history' } },
        ],
      },
    ],
  },
  { path: '*', element: <Navigate to="/dashboard" replace /> },
]);
